import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, API_BASE, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast, Toaster } from "sonner";
import {
  Play, Pause, CheckCircle2, Lock, Clock, Sparkles, ShieldAlert, Loader2,
  Award, Volume2, VolumeX, Video, TrendingUp,
} from "lucide-react";

function resolveVideoUrl(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  // `API_BASE` = `${REACT_APP_BACKEND_URL}/api`, so prepending it to a
  // `/media/...` path naturally yields `/api/media/...` — which is the only
  // path shape the k8s ingress routes to the backend. If the DB already
  // stores a `/api/media/...` path (from a future migration), strip the
  // leading `/api` since API_BASE will re-add it.
  if (u.startsWith("/api/media/")) return `${API_BASE}${u.slice(4)}`;
  if (u.startsWith("/media/")) return `${API_BASE}${u}`;
  return u;
}

function CounterWheel({ done, total }) {
  const pct = total > 0 ? Math.min(1, done / total) : 0;
  const circ = 2 * Math.PI * 34;
  const dash = pct * circ;
  return (
    <div className="relative w-24 h-24" data-testid="ad-counter-wheel">
      <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
        <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
        <circle
          cx="40" cy="40" r="34" fill="none"
          stroke="#FBBF24" strokeWidth="6" strokeLinecap="round"
          strokeDasharray={`${dash} ${circ - dash}`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-xl font-black text-[#FBBF24] font-mono-num leading-none">{done}<span className="text-white/40 text-sm">/{total}</span></div>
        <div className="text-[9px] uppercase tracking-widest text-white/50 mt-0.5">Completed</div>
      </div>
    </div>
  );
}

function CrimsonBar({ progress }) {
  return (
    <div className="relative h-1.5 bg-white/10 rounded-full overflow-hidden" data-testid="ad-progress-bar">
      <div
        className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#DC2626] via-[#F87171] to-[#DC2626] transition-[width] duration-100 ease-linear"
        style={{ width: `${Math.min(100, Math.max(0, progress * 100)).toFixed(2)}%` }}
      />
    </div>
  );
}

function EarnBanner({ visible, amount }) {
  if (!visible) return null;
  return (
    <div
      className="fixed top-4 right-4 z-[60] glass-card border border-[#FBBF24]/60 px-4 py-2.5 flex items-center gap-2 shadow-[0_20px_50px_-15px_rgba(251,191,36,0.6)] animate-in slide-in-from-top duration-300"
      data-testid="ad-earn-banner"
    >
      <Award size={16} className="text-[#FBBF24]" />
      <span className="text-white font-bold text-sm">
        +<span className="font-mono-num text-[#FBBF24]">{amount}</span> ZenCoins Earned!
      </span>
    </div>
  );
}

function VideoPlayer({ video, onCompleted, onCancel }) {
  const videoRef = useRef(null);
  const startedAtRef = useRef(null);
  const [watchToken, setWatchToken] = useState("");
  const [busy, setBusy] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [visibilityPaused, setVisibilityPaused] = useState(false);
  const [muted, setMuted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const completedRef = useRef(false);

  // Server-issued start
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      setError("");
      try {
        const { data } = await api.post(`/ads/videos/${video.id}/start`);
        if (cancelled) return;
        setWatchToken(data.watch_token);
        startedAtRef.current = Date.now();
      } catch (e) {
        if (!cancelled) setError(formatErr(e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [video.id]);

  // Page Visibility API — auto-pause when tab hidden / window minimized.
  useEffect(() => {
    const onVis = () => {
      const v = videoRef.current;
      if (document.hidden) {
        if (v && !v.paused) v.pause();
        setVisibilityPaused(true);
      } else {
        setVisibilityPaused(false);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const handleComplete = useCallback(async () => {
    if (completedRef.current || submitting || !watchToken) return;
    completedRef.current = true;
    setSubmitting(true);
    try {
      const { data } = await api.post("/ads/videos/complete", { watch_token: watchToken });
      onCompleted?.(data);
    } catch (e) {
      completedRef.current = false;
      setError(formatErr(e));
      toast.error(formatErr(e));
    } finally {
      setSubmitting(false);
    }
  }, [submitting, watchToken, onCompleted]);

  const onLoadedMeta = () => {
    // Nothing — server-driven duration is authoritative. Reset progress.
    setProgress(0);
    setElapsed(0);
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    const p = v.duration > 0 ? v.currentTime / v.duration : 0;
    setProgress(p);
    setElapsed(v.currentTime);
  };

  const onEnded = () => {
    setPlaying(false);
    handleComplete();
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  // Anti-scrub: continuously reset any currentTime jump
  const lastTimeRef = useRef(0);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const guard = () => {
      // If forward jump is larger than allowed drift, snap back.
      const cur = v.currentTime;
      const last = lastTimeRef.current;
      if (cur - last > 1.25) {
        v.currentTime = last;
      } else {
        lastTimeRef.current = cur;
      }
    };
    v.addEventListener("seeking", guard);
    v.addEventListener("seeked", guard);
    return () => {
      v.removeEventListener("seeking", guard);
      v.removeEventListener("seeked", guard);
    };
  }, [watchToken]);

  return (
    <div className="glass-card gold-border p-4 md:p-5" data-testid={`ad-player-${video.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">{video.partner_name}</div>
          <h2 className="text-lg md:text-xl font-black tracking-tight text-white mt-0.5 truncate">{video.title}</h2>
        </div>
        <button onClick={onCancel} className="text-white/60 hover:text-white text-xs" data-testid="ad-player-close">Back to list</button>
      </div>

      <div className="relative bg-black rounded-lg overflow-hidden border border-white/8" style={{ aspectRatio: "16/9" }}>
        <video
          ref={videoRef}
          src={resolveVideoUrl(video.video_url)}
          className="w-full h-full object-cover pointer-events-none"
          controls={false}
          playsInline
          preload="metadata"
          muted={muted}
          onLoadedMetadata={onLoadedMeta}
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          data-testid="ad-player-video"
        />
        {/* Play/Pause overlay */}
        {!playing && !visibilityPaused && !submitting && !busy && !error && (
          <button
            onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/25 transition"
            data-testid="ad-player-play"
          >
            <div className="w-16 h-16 rounded-full bg-[#DC2626] flex items-center justify-center shadow-[0_20px_50px_-15px_rgba(220,38,38,0.9)]">
              <Play size={26} className="text-white ml-1" />
            </div>
          </button>
        )}
        {visibilityPaused && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/75 text-center px-6" data-testid="ad-player-visibility-pause">
            <ShieldAlert size={26} className="text-[#FBBF24] mb-2" />
            <div className="font-bold text-white">Playback paused — window is hidden.</div>
            <div className="text-xs text-white/60 mt-1">Return to this tab to continue earning. The timer is frozen.</div>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white/70 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Preparing session…
          </div>
        )}
        {submitting && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-white gap-2" data-testid="ad-player-submitting">
            <Loader2 size={18} className="animate-spin" /> Validating completion…
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/85 text-center px-6" data-testid="ad-player-error">
            <ShieldAlert size={22} className="text-[#FCA5A5] mb-2" />
            <div className="text-[#FCA5A5] font-bold text-sm">{error}</div>
            <button onClick={onCancel} className="mt-3 text-white/70 hover:text-white text-xs underline">Return to list</button>
          </div>
        )}
      </div>

      <div className="mt-3">
        <CrimsonBar progress={progress} />
        <div className="flex items-center justify-between text-[11px] mt-2">
          <div className="flex items-center gap-2 text-white/60">
            <Clock size={12} /> <span className="font-mono-num">{elapsed.toFixed(1)}s / {video.duration_seconds}s</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setMuted((m) => !m)} className="text-white/50 hover:text-white" title="Toggle mute" data-testid="ad-player-mute">
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            <button onClick={togglePlay} className="text-white/50 hover:text-white" title={playing ? "Pause" : "Play"} data-testid="ad-player-toggle">
              {playing ? <Pause size={14} /> : <Play size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VideoCard({ v, disabled, onSelect }) {
  const watched = !!v.watched_today;
  return (
    <button
      onClick={() => !disabled && !watched && onSelect(v)}
      disabled={disabled || watched}
      className={`text-left glass-card p-3 border transition-all ${watched ? "border-emerald-400/30 opacity-70" : disabled ? "border-white/8 opacity-50 cursor-not-allowed" : "border-white/10 hover:border-[#FBBF24]/50 hover:bg-white/[0.03]"}`}
      data-testid={`ad-video-card-${v.id}`}
    >
      <div className="relative bg-black rounded-md overflow-hidden mb-2" style={{ aspectRatio: "16/9" }}>
        {v.thumbnail_url ? (
          <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/25">
            <Video size={30} />
          </div>
        )}
        <div className="absolute bottom-1 right-1 bg-black/70 text-[10px] px-1.5 py-0.5 rounded font-mono-num text-white">{v.duration_seconds}s</div>
        {watched && (
          <div className="absolute inset-0 bg-emerald-950/70 flex items-center justify-center">
            <div className="inline-flex items-center gap-1.5 text-emerald-300 font-bold text-xs">
              <CheckCircle2 size={16} /> Watched Today
            </div>
          </div>
        )}
        {disabled && !watched && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <div className="inline-flex items-center gap-1.5 text-white/60 font-bold text-xs">
              <Lock size={14} /> Daily quota reached
            </div>
          </div>
        )}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold truncate">{v.partner_name}</div>
      <div className="text-sm font-bold text-white truncate mt-0.5">{v.title}</div>
    </button>
  );
}

export default function AdPortal() {
  const { user, refresh } = useAuth();
  const [state, setState] = useState(null); // {settings, status, videos}
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [earn, setEarn] = useState({ visible: false, amount: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ads/portal");
      setState(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCompleted = async (result) => {
    setEarn({ visible: true, amount: result.coins_awarded || 0 });
    setTimeout(() => setEarn({ visible: false, amount: 0 }), 2400);
    setSelected(null);
    await load();
    await refresh?.();
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-white/50 gap-2" data-testid="ad-portal-loading">
        <Loader2 size={18} className="animate-spin" /> Loading Ad Station…
      </div>
    );
  }
  if (!state) return null;

  const { settings, status, videos } = state;
  const dailyLimitReached = status.remaining_today <= 0;

  return (
    <div className="space-y-6" data-testid="ad-portal-root">
      <Toaster position="top-right" richColors />
      <EarnBanner visible={earn.visible} amount={earn.amount} />

      {/* Header stats row */}
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-4 items-center glass-card gold-border p-5" data-testid="ad-portal-header">
        <CounterWheel done={status.watched_today} total={settings.max_daily_ads} />
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold flex items-center gap-1">
            <Sparkles size={12} /> Marketing Center · Watch to Earn ZenCoins
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-1">
            {dailyLimitReached ? "Daily Quota Complete" : "Watch. Earn. Redeem."}
          </h1>
          <p className="text-white/60 text-sm mt-1">
            Earn <span className="font-mono-num text-[#FBBF24] font-bold">{settings.coins_per_watch} ZenCoins</span> per verified {settings.min_duration_seconds || 30}–{settings.max_duration_seconds || 60}s view.
            Redeem at checkout for up to <span className="font-mono-num text-[#FBBF24] font-bold">{Number(settings.max_coin_discount_pct_per_order).toFixed(0)}%</span> off.
            Quota resets at <strong className="text-white/80">UTC midnight</strong>.
          </p>
          <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1.5">
            ZenCoins are non-withdrawable · expire {settings.coin_expiry_months} months after earning
          </div>
        </div>
        <div className="rounded-lg border border-[#FBBF24]/30 bg-black/40 p-3 min-w-[180px]" data-testid="ad-portal-accum">
          <div className="text-[10px] uppercase tracking-widest text-white/45 font-bold">ZenCoins Balance</div>
          <div className="font-mono-num text-2xl font-black text-[#FBBF24] mt-0.5" data-testid="ad-portal-coin-balance">
            {status.zencoin_balance} <span className="text-xs text-white/50">ZC</span>
          </div>
          <div className="text-[10px] text-white/45 mt-1">
            ≈ <span className="text-white/70 font-mono-num">₱{Number(status.zencoin_php_value).toFixed(2)}</span> in discount value
          </div>
          <div className="text-[10px] text-white/45 mt-1">
            This month: <span className="font-mono-num text-white/70">{status.coins_redeemed_this_month}</span> / <span className="font-mono-num">{settings.max_coins_per_month}</span> redeemed
          </div>
        </div>
      </div>

      {selected ? (
        <VideoPlayer video={selected} onCompleted={onCompleted} onCancel={() => setSelected(null)} />
      ) : videos.length === 0 ? (
        <div className="glass-card p-10 text-center text-white/55" data-testid="ad-portal-empty">
          No videos are live right now. Check back soon — the CEO&apos;s bulletin drops daily.
        </div>
      ) : (
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 font-bold mb-2 flex items-center gap-1">
            <Video size={11} /> Today&apos;s Line-Up · {videos.length} spots available
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="ad-portal-grid">
            {videos.map((v) => (
              <VideoCard key={v.id} v={v} disabled={dailyLimitReached} onSelect={setSelected} />
            ))}
          </div>
        </div>
      )}

      <div className="text-[10px] uppercase tracking-[0.22em] text-white/25 text-center pt-2">
        Anti-fraud: playback pauses when the tab hides. Scrubbing is disabled. Server verifies elapsed time against the video&apos;s exact duration.
      </div>
    </div>
  );
}
